import { useEffect, useMemo, useRef, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useObjectives, type ObjectiveFilters } from "@/hooks/use-objectives";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useTactics } from "@/hooks/use-tactics";
import { useCollegeGoals } from "@/hooks/use-college-goals";
import { useCollegeStrategicPillars } from "@/hooks/use-college-strategic-pillars";
import { useCurrentUser } from "@/hooks/use-current-user";
import { DEFAULT_YEAR } from "@/lib/year-options";
import { stripDueDatePrefix, stripPdfSourceAttribution } from "@/lib/kr-description";
import { getKrTypeLabel } from "@/lib/status-machine";
import { formatTimestamp } from "@/lib/date";
import type { KeyResult, Objective, Quarter } from "@/types/entities";
import {
  computeExecStats,
  describeCount,
  describeKrProgress,
  formatCollegeAlignment,
  formatFocusList,
  formatPillarLabel,
  formatStrategicInitiativeList,
  getFullStatusLabel,
  getQuarterDisplay,
  getShortStatusLabel,
  getStatusClass,
  objectiveLabelNumber,
  resolveAlignment,
  shortTitle,
  type PrintLookups,
} from "./objectives-print-helpers";
import "./objectives-print.css";

const QUARTER_ORDER: Record<Quarter, number> = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
const PRINT_PAGE_LIMIT = 200;
const COVER_LOGO = "/print-assets/logo-vertical-white.png";
const RUNNER_LOGO = "/print-assets/logo-horizontal-blue.png";

function sortKrs(krs: KeyResult[]): KeyResult[] {
  return [...krs].sort((a, b) => {
    const q = QUARTER_ORDER[a.quarter] - QUARTER_ORDER[b.quarter];
    if (q !== 0) return q;
    return a.title.localeCompare(b.title);
  });
}

export function ObjectivesPrintPage() {
  const { currentUser } = useCurrentUser();
  const search = useSearch({ from: "/objectives/print" });

  const filters: ObjectiveFilters = currentUser
    ? {
        year: search.year ?? DEFAULT_YEAR,
        level: search.level,
        status: search.status,
        type: search.type,
        focus_area_id: search.focus_area_id,
        owner_id: search.owner_id ?? currentUser.id,
        limit: PRINT_PAGE_LIMIT,
      }
    : {};

  const { data: objectivesData, isLoading: objectivesLoading, error: objectivesError } =
    useObjectives(filters);
  const { data: focusAreasData } = useFocusAreas();
  const { data: tacticsData } = useTactics();
  const { data: collegeGoalsData } = useCollegeGoals();
  const { data: pillarsData } = useCollegeStrategicPillars();

  const objectives = useMemo(() => objectivesData?.data ?? [], [objectivesData]);
  const truncated = Boolean(objectivesData?.next_cursor);

  const lookups: PrintLookups = useMemo(
    () => ({
      focusAreas: focusAreasData?.data ?? [],
      tactics: tacticsData?.data ?? [],
      collegeGoals: collegeGoalsData?.data ?? [],
      pillars: pillarsData?.data ?? [],
    }),
    [focusAreasData, tacticsData, collegeGoalsData, pillarsData],
  );

  const alignmentMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof resolveAlignment>>();
    for (const obj of objectives) map.set(obj.id, resolveAlignment(obj, lookups));
    return map;
  }, [objectives, lookups]);

  const krQueries = useQueries({
    queries: objectives.map((obj) => ({
      queryKey: ["key-results", { objective_id: obj.id }],
      queryFn: async () => {
        const { data, error } = await apiClient.GET("/v1/key-results", {
          params: { query: { objective_id: obj.id, limit: PRINT_PAGE_LIMIT } },
        });
        if (error) throw new Error(error.error.message);
        return data;
      },
    })),
  });

  const krMap = useMemo(() => {
    const map = new Map<string, KeyResult[]>();
    objectives.forEach((obj, i) => {
      const q = krQueries[i];
      if (q?.data?.data) {
        map.set(obj.id, sortKrs(q.data.data as KeyResult[]));
      }
    });
    return map;
  }, [objectives, krQueries]);

  const allKrsSettled =
    objectives.length === 0 || krQueries.every((q) => !q.isPending);
  const ready =
    !!currentUser && !objectivesLoading && !objectivesError && allKrsSettled;

  const printedRef = useRef(false);
  const [autoPrintBlocked, setAutoPrintBlocked] = useState(false);

  useEffect(() => {
    if (!ready || printedRef.current) return;
    if (objectives.length === 0) {
      setAutoPrintBlocked(true);
      return;
    }
    printedRef.current = true;

    const t = window.setTimeout(() => {
      try {
        window.print();
      } catch {
        setAutoPrintBlocked(true);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [ready, objectives.length]);

  const headerOwnerName = useMemo(() => {
    if (!objectives.length) return currentUser?.name ?? "";
    const ownerId = filters.owner_id;
    const match = objectives.find((o) => o.owner_id === ownerId);
    return match?.owner_name ?? currentUser?.name ?? "";
  }, [objectives, filters.owner_id, currentUser]);

  const headerYear = filters.year ?? DEFAULT_YEAR;
  const generatedAt = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const stats = useMemo(() => computeExecStats(objectives), [objectives]);

  if (objectivesError) {
    return (
      <div className="okr-print-doc">
        <div className="mx-auto max-w-3xl p-8 text-sm text-red-700">
          Failed to load objectives: {objectivesError.message}
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="okr-print-doc">
        <div className="print-controls print-status">
          <div>Preparing document…</div>
        </div>
      </div>
    );
  }

  if (objectives.length === 0) {
    return (
      <div className="okr-print-doc">
        <div className="print-controls print-status">
          <div>No objectives match the current filters.</div>
          <button type="button" onClick={() => window.history.back()}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const totalCountWord = describeCount(objectives.length);
  const krCountWord = describeCount(stats.krCount);
  const ownerLabel = headerOwnerName || "ITS";
  const reportRunnerTitle = `ITS · Year ${headerYear} OKR Report`;

  return (
    <div className="okr-print-doc">
      <div className="print-controls print-status">
        <div>
          {autoPrintBlocked
            ? "Ready. Click Print to open the print dialog."
            : "Opening print dialog…"}
          {truncated && (
            <span className="print-status">
              {" "}
              · More objectives exist than fit on this report (limit {PRINT_PAGE_LIMIT}).
            </span>
          )}
        </div>
        <button type="button" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <main className="book">
        <CoverPage
          year={headerYear}
          ownerName={ownerLabel}
          objectiveCount={objectives.length}
          objectiveCountWord={totalCountWord}
          krCount={stats.krCount}
          krCountWord={krCountWord}
          generatedAt={generatedAt}
        />

        <ExecSummaryPage
          year={headerYear}
          ownerName={ownerLabel}
          objectives={objectives}
          alignmentMap={alignmentMap}
          stats={stats}
          runnerTitle={reportRunnerTitle}
        />

        <ContentsPage
          ownerName={ownerLabel}
          objectives={objectives}
          runnerTitle={reportRunnerTitle}
        />

        {objectives.map((obj, idx) => (
          <ObjectiveDetailPage
            key={obj.id}
            objective={obj}
            index={idx + 1}
            total={objectives.length}
            alignment={alignmentMap.get(obj.id)!}
            krs={krMap.get(obj.id) ?? []}
          />
        ))}
      </main>
    </div>
  );
}

interface CoverProps {
  year: number;
  ownerName: string;
  objectiveCount: number;
  objectiveCountWord: string;
  krCount: number;
  krCountWord: string;
  generatedAt: string;
}

function CoverPage({
  year,
  ownerName,
  objectiveCount,
  objectiveCountWord,
  krCount,
  krCountWord,
  generatedAt,
}: CoverProps) {
  return (
    <section className="page cover">
      <div className="corner">
        Confidential · Internal
        <br />
        Office of the AVP, Information Technology Services
      </div>

      <div className="top">
        <div className="crest-block">
          <img src={COVER_LOGO} alt="Hillsdale College" />
        </div>
      </div>

      <div className="center">
        <div className="eyebrow">
          <span className="dash" aria-hidden="true" />
          OKR Report · Year {year}
        </div>
        <h1>
          ITS Performance Intelligence Platform
        </h1>
        <p className="lede">
          A consolidated view of the {objectiveCountWord || objectiveCount}{" "}
          {objectiveCount === 1 ? "objective" : "objectives"} and{" "}
          {krCountWord || krCount} {krCount === 1 ? "key result" : "key results"}{" "}
          guiding Information Technology Services through the {year} year — measuring
          how ITS protects the College, serves its people, and advances the College's
          strategic pillars.
        </p>
      </div>

      <div className="meta-grid">
        <div className="cell">
          <div className="label">Owner</div>
          <div className="value">{ownerName}</div>
        </div>
        <div className="cell">
          <div className="label">Department</div>
          <div className="value">ITS</div>
        </div>
        <div className="cell">
          <div className="label">Reporting Period</div>
          <div className="value">Year {year}</div>
        </div>
        <div className="cell">
          <div className="label">Generated</div>
          <div className="value mono">{generatedAt}</div>
        </div>
      </div>
    </section>
  );
}

interface ExecSummaryProps {
  year: number;
  ownerName: string;
  objectives: Objective[];
  alignmentMap: Map<string, ReturnType<typeof resolveAlignment>>;
  stats: ReturnType<typeof computeExecStats>;
  runnerTitle: string;
}

function ExecSummaryPage({
  year,
  ownerName,
  objectives,
  alignmentMap,
  stats,
  runnerTitle,
}: ExecSummaryProps) {
  const draftLine = stats.draftCount > 0 ? `${stats.draftCount} in Draft` : "none in Draft";
  const cumulativeText =
    stats.cumulativePct == null ? "—" : `${stats.cumulativePct}`;
  return (
    <section className="page">
      <header className="runner header">
        <span>{runnerTitle}</span>
        <img src={RUNNER_LOGO} className="crest" alt="" />
      </header>

      <div className="page-head">
        <div>
          <h2>Executive Summary</h2>
        </div>
        <div className="ornament">§</div>
      </div>

      <div className="exec-stats">
        <div className="stat">
          <div className="num">{stats.objectiveCount}</div>
          <div className="lbl">
            Objectives
            <br />
            Year {year}
          </div>
        </div>
        <div className="stat">
          <div className="num">{stats.krCount}</div>
          <div className="lbl">
            Key Results
            <br />
            across the year
          </div>
        </div>
        <div className="stat">
          <div className="num">
            {stats.inProgressCount} <small>/ {stats.objectiveCount}</small>
          </div>
          <div className="lbl">
            In Progress
            <br />
            {draftLine}
          </div>
        </div>
        <div className="stat">
          <div className="num">
            {cumulativeText}
            {stats.cumulativePct == null ? null : <small>%</small>}
          </div>
          <div className="lbl">
            Cumulative
            <br />
            weighted progress
          </div>
        </div>
      </div>

      <div className="rule-mark">
        <span className="mark">❦</span>
      </div>

      <div className="obj-list">
        {objectives.map((obj, idx) => {
          const alignment = alignmentMap.get(obj.id);
          const pillar = alignment ? formatPillarLabel(alignment) : null;
          const krTotal = obj.kr_total ?? 0;
          const metaParts: string[] = [];
          if (pillar) metaParts.push(pillar);
          metaParts.push(`${krTotal} ${krTotal === 1 ? "KR" : "KRs"}`);
          const pct = obj.completion_percent ?? 0;
          const isZero = pct <= 0;
          return (
            <div key={obj.id} className="item">
              <div className="num">{idx + 1}</div>
              <div className="body">
                <div className="title">{obj.title}</div>
                <div className="meta">{metaParts.join(" · ")}</div>
              </div>
              <div>
                <span className={`pill ${getStatusClass(obj.status)}`}>
                  <span className="dot" aria-hidden="true" />
                  {getFullStatusLabel(obj.status)}
                </span>
                <div className={`bar${isZero ? " zero" : ""}`}>
                  <span style={{ width: isZero ? undefined : `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <footer className="runner footer">
        <span>Owner · {ownerName}</span>
      </footer>
    </section>
  );
}

interface ContentsProps {
  ownerName: string;
  objectives: Objective[];
  runnerTitle: string;
}

function ContentsPage({ ownerName, objectives, runnerTitle }: ContentsProps) {
  return (
    <section className="page">
      <header className="runner header">
        <span>{runnerTitle}</span>
        <img src={RUNNER_LOGO} className="crest" alt="" />
      </header>

      <div className="page-head">
        <div>
          <div className="kicker">II · Navigation</div>
          <h2>Contents</h2>
        </div>
        <div className="ornament">§</div>
      </div>

      <ol className="toc-list">
        {objectives.map((obj, idx) => (
          <li key={obj.id}>
            <span className="n">{idx + 1}</span>
            <span className="t">{obj.title}</span>
          </li>
        ))}
      </ol>

      <footer className="runner footer">
        <span>Owner · {ownerName}</span>
      </footer>
    </section>
  );
}

interface DetailProps {
  objective: Objective;
  index: number;
  total: number;
  alignment: ReturnType<typeof resolveAlignment>;
  krs: KeyResult[];
}

function ObjectiveDetailPage({ objective, index, total, alignment, krs }: DetailProps) {
  const pillarLabel = formatPillarLabel(alignment) ?? "—";
  const collegeAlignment = formatCollegeAlignment(alignment);
  const focusList = formatFocusList(alignment);
  const siList = formatStrategicInitiativeList(alignment);
  const krTotal = objective.kr_total ?? krs.length;
  const krDone = objective.kr_completed ?? 0;
  const objPct =
    objective.completion_percent == null ? null : Math.round(objective.completion_percent);
  const isZeroPct = objPct == null || objPct <= 0;
  const tagNumber = objectiveLabelNumber(index);

  return (
    <section className="page obj-page">
      <header className="runner header">
        <span>
          Objective {tagNumber} · {pillarLabel}
        </span>
        <img src={RUNNER_LOGO} className="crest" alt="" />
      </header>

      <div className="num-tag">
        <span className="roman">{tagNumber}</span>
        <span className="of">
          of {describeCount(total)} · {pillarLabel}
        </span>
      </div>

      <h1 className="obj-title">{objective.title}</h1>

      <div className="meta-strip">
        <div className="m">
          <div className="l">Status</div>
          <div className="v small">
            <span className={`status-tag ${getStatusClass(objective.status)}`}>
              <span className="dot" aria-hidden="true" />
              {getFullStatusLabel(objective.status)} ·{" "}
              {objective.type === "committed" ? "Committed" : "Aspirational"}
            </span>
          </div>
        </div>
        <div className="m">
          <div className="l">Period</div>
          <div className="v">Year {objective.year}</div>
        </div>
        <div className="m">
          <div className="l">Owner</div>
          <div className="v">{objective.owner_name ?? "—"}</div>
        </div>
        <div className="m">
          <div className="l">Key Results</div>
          <div className="v">{krTotal}</div>
        </div>
        <div className="m">
          <div className="l">Last updated</div>
          <div className="v">{formatTimestamp(objective.updated_at)}</div>
        </div>
      </div>

      <div className="alignment">
        <div className="col">
          <div className="l">College Alignment</div>
          <div className="v">{collegeAlignment ?? "—"}</div>
        </div>
        <div className="col">
          <div className="l">ITS Focus</div>
          <div className="v">{focusList ?? "—"}</div>
        </div>
        <div className="col">
          <div className="l">
            {alignment.tactics.length > 1 ? "Strategic Initiatives" : "Strategic Initiative"}
          </div>
          <div className="v si">{siList ?? "—"}</div>
        </div>
      </div>

      <div className="obj-progress">
        <div className="pct">
          {objPct == null ? "—" : objPct}
          {objPct != null && <span className="sym">%</span>}
        </div>
        <div className="lbl">
          <strong>Cumulative progress</strong>
          {krDone} of {krTotal} key {krTotal === 1 ? "result" : "results"} closed
        </div>
        <div className={`obj-bar${isZeroPct ? " zero" : ""}`}>
          <span style={{ width: isZeroPct ? undefined : `${Math.min(100, objPct ?? 0)}%` }} />
        </div>
      </div>

      <div className="kr-section">
        <div className="kr-head">
          <span className="label">Key Results</span>
          <span className="count">— {krs.length}</span>
          <span className="line" />
        </div>
        {krs.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--print-navy-30)" }}>
            No key results.
          </p>
        ) : (
          <table className="kr-table">
            <thead>
              <tr>
                <th className="num-col">#</th>
                <th className="kr-col">Key Result</th>
                <th className="qtr-col">Quarter</th>
                <th className="type-col">Type</th>
                <th className="status-col">Status</th>
                <th className="progress-col">Progress</th>
                <th className="due-col">Due</th>
                <th className="updated-col">Updated</th>
              </tr>
            </thead>
            <tbody>
              {krs.map((kr, i) => (
                <KrRow key={kr.id} kr={kr} index={i + 1} year={objective.year} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <footer className="runner footer">
        <span>
          Objective {tagNumber} · {shortTitle(objective.title)}
        </span>
      </footer>
    </section>
  );
}

interface KrRowProps {
  kr: KeyResult;
  index: number;
  year: number;
}

function KrRow({ kr, index, year }: KrRowProps) {
  const desc = stripDueDatePrefix(kr.title);
  const note = stripPdfSourceAttribution(kr.description ?? "").trim();
  const quarter = getQuarterDisplay(kr.quarter, year);
  const progress = describeKrProgress(kr);
  const typeClass = kr.type === "binary" ? "binary" : "";

  return (
    <tr>
      <td className="num-col">
        <span className="n">{index}</span>
      </td>
      <td className="kr-col">
        <div className="desc">{desc}</div>
        {note && <div className="note">{note}</div>}
      </td>
      <td className="qtr-col">
        <span className="qtr-tag">
          {quarter.calendar.split(" ")[0]}{" "}
          <span className="fy">
            {quarter.calendar.split(" ").slice(1).join(" ")}
            {quarter.fiscal ? ` · ${quarter.fiscal}` : ""}
          </span>
        </span>
      </td>
      <td className="type-col">
        <span className={`type-tag${typeClass ? ` ${typeClass}` : ""}`}>
          {getKrTypeLabel(kr.type)}
        </span>
      </td>
      <td className="status-col">
        <span className={`status-tag ${getStatusClass(kr.status)}`}>
          <span className="dot" aria-hidden="true" />
          {getShortStatusLabel(kr.status)}
        </span>
      </td>
      <td className="progress-col">
        <div className="kr-progress">
          <div className={`frac${progress.isNotDone ? " notdone" : ""}`}>
            {progress.primaryText}
            {progress.pctText && <span className="pct">{progress.pctText}</span>}
          </div>
          <div className={`kr-bar${progress.barWidth <= 0 ? " zero" : ""}`}>
            <span
              style={{ width: progress.barWidth <= 0 ? undefined : `${progress.barWidth}%` }}
            />
          </div>
        </div>
      </td>
      <td className="due-col">{kr.due_date ?? "—"}</td>
      <td className="updated-col">{formatTimestamp(kr.updated_at) || "—"}</td>
    </tr>
  );
}
